<template>
  <v-main>
    <v-container flex-column fill-height>
      <v-layout>
        <v-flex class="registration">
          <v-card outlined>
            <div v-if="registered">
              Please check your email, you will get an account activation email
              shortly.
            </div>

            <v-form ref="form" v-else>
              <div class="registration--form">
                <div class="registration__header">
                  <div class="registration__header-logo">
                    <img src="/img/brand/apio-symbol.svg" width="52px" />
                    <div>apio</div>
                  </div>
                  <div class="registration__reset-text">
                    <div class="h1 black--text">
                      Get started with a free account
                    </div>
                    <div class="h3 registration__reset-text--subtitle">
                      Take control of your integrations today.
                    </div>
                  </div>
                </div>

                <div class="d-flex justify-center">
                  <LoginButton type="google" label="Register with Google" />
                </div>
                <div class="d-flex justify-center">
                  <div class="h3 mt-3 mb-3">or</div>
                </div>

                <div class="small-extra-bold apioshade2--text">Full name</div>
                <form-group :validations="$v.name" class="form-group">
                  <div slot-scope="{ errors }">
                    <v-text-field
                      type="name"
                      :error="true"
                      dense
                      outlined
                      required
                      v-model="$v.name.$model"
                      placeholder="Type here..."
                      :hide-details="true"
                    >
                    </v-text-field>
                    <div class="form-field-error">{{ errors[0] || "" }}</div>
                  </div>
                </form-group>
                <form-group :validations="$v.emailaddr" class="form-group">
                  <div slot-scope="{ errors }">
                    <div class="small-extra-bold apioshade2--text">
                      Email address
                    </div>
                    <v-text-field
                      type="email"
                      ref="emailaddr"
                      :error="true"
                      dense
                      outlined
                      required
                      v-model="$v.emailaddr.$model"
                      placeholder="Type here..."
                      :hide-details="true"
                    >
                    </v-text-field>
                    <div class="form-field-error">{{ errors[0] || "" }}</div>
                  </div>
                </form-group>

                <form-group :validations="$v.new_pass" class="form-group">
                  <div slot-scope="{ errors }">
                    <div class="small-extra-bold apioshade2--text">
                      Password
                    </div>
                    <v-text-field
                      :error="true"
                      dense
                      type="password"
                      outlined
                      required
                      v-model="$v.new_pass.$model"
                      placeholder="Type here..."
                      :hide-details="true"
                    >
                    </v-text-field>
                    <password
                      v-model="new_pass"
                      @score="score"
                      :strength-meter-only="true"
                    />
                    <div class="tiny registration__weak-strong">
                      <div>Weak</div>
                      <div>Strong</div>
                    </div>

                    <div class="form-field-error">{{ errors[0] || "" }}</div>
                  </div>
                </form-group>

                <p class="tiny mb-6">
                  Minimum 8 characters. Must include lowercase, upper case and a
                  number.
                </p>

                <apio-checkbox
                  class="mb-8"
                  label=""
                  :value="termsOfUse"
                  :checked="termsOfUse"
                  v-model="termsOfUse"
                >
                  <div slot="checkbox-label">
                    By signing up to Apio you agree to the
                    <a
                      href="https://www.getapio.com/terms-conditions"
                      target="_blank"
                      >terms of use.</a
                    >
                  </div>
                </apio-checkbox>

                <div class="apioshade2--text mb-6">
                  Enjoy all the latest tips and news from Apio by signing up to
                  our newsletter.
                </div>

                <apio-checkbox
                  class="mb-8"
                  label="Yes please, sign me up for all the up-to-date news
                  from Apio."
                  :value="newsletter"
                  :checked="newsletter"
                  v-model="newsletter"
                />

                <p class="text-danger" v-if="message">{{ message }}</p>
                <div class="registration__actions">
                  <apio-button
                    :disabled="!isFormValid"
                    class="registration__actions-getstarted"
                    @click="getStarted"
                    >Get started!</apio-button
                  >
                  <div>I already have an account</div>
                  <apio-button
                    class="registration__actions-signin"
                    button-type="secondary"
                    @click="goToLogin"
                    >Sign in</apio-button
                  >
                </div>
              </div>
            </v-form>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
    <apio-snackbar
      :text="snackBarText"
      :status="snackBarStatus"
      :snackbar="snackbar"
      @close="snackbar = false"
    />
  </v-main>
</template>

<script>
import request from '@/API/';
import { required, minLength, email } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";

export default {
  name: "Registration",
  components: {
    LoginButton: () => import("@/components/Account/LoginButton.vue"),
    Password: () => import("vue-password-strength-meter"),
    ApioButton: () => import("@/components/Misc/Button.vue"),
    FormGroup: () => import("@/pages/Account/FormGroup"),
  },
  validations: {
    name: {
      required,
      minLength: minLength(2),
    },
    emailaddr: {
      required,
      email,
    },
    new_pass: {
      required,
      minLength: minLength(8),
    },
  },
  mixins: [validationMixin],
  data() {
    return {
      name: "",
      new_pass: "",
      emailaddr: "",
      message: "",
      registered: false,
      pwscore: 0,
      termsOfUse: false,
      newsletter: true,
      snackBarText: "",
      snackBarStatus: "",
      snackbar: false,
    };
  },
  computed: {
    isFormValid() {
      return !this.$v.$invalid && this.pwscore > 3 && this.termsOfUse;
    },
  },
  methods: {
    sso(){
      var serverURL = "http%3A%2F%2Flocalhost%3A8081%2F"
      if (process.env.VUE_APP_BASE_URL != "/") {
        serverURL = encodeURIComponent(process.env.VUE_APP_BASE_URL)
      }
      window.location.href = `https://accounts.google.com/o/oauth2/auth?redirect_uri=${serverURL}%2Fuser%2Fsso&response_type=code&client_id=882582985236-mvffdtgngj8voumat6ue8kd3de6nllvv.apps.googleusercontent.com&scope=profile%20email&prompt=select_account&login_hint=`
    },
    score(s) {
      this.pwscore = s;
    },
    goToLogin() {
      this.$router.push("/pages/login");
    },
    getStarted() {
      if (!this.$v.invalid) {
        this.createAccount();
      }
    },
    createAccount() {
      const vm = this;
      request
        .post("/user/registration", {
          name: this.name,
          email: this.emailaddr,
          password: this.new_pass,
          newsletter: this.newsletter,
        })
        .then((res) => {
          if (res.data.success) {
            this.registered = true;
            return;
          }
          return res;
        })
        .catch((err) => {
          vm.snackBarText = `Could not register user. ${err}`;
          vm.snackBarStatus = "error";
          vm.snackbar = true;
        });
    },
    status(validation) {
      return {
        error: validation.$error,
        dirty: validation.$dirty,
      };
    },
  },
};
</script>

<style scoped lang="scss">
// .apio {
//   &__checkbox {
//     display: flex;
//     align-items: center;
//     margin-bottom: 32px;
//   }
// }

::v-deep .Password {
  max-width: 760px;
  &__strength-meter {
    margin: 10px 0 4px;
  }
}

apio-button {
  text-transform: none !important;
}

::v-deep .v-card__actions {
  flex-direction: column;
}

.theme--light.v-application {
  background: transparent;
}

.form-field-error {
  display: flex;
  visibility: visible;
  height: 24px;
}

.h3 {
  font-weight: normal;
  color: var(--apio-shade-2);
  margin-bottom: 12px;
}

.h1 {
  color: var(--apio-shade-2);
  margin-bottom: 8px;
}

.v-messages {
  position: absolute !important;
  top: -16px;
}

.v-input__prepend-inner {
  height: 16px !important;
}

.v-sheet--outlined {
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
}

.v-sheet.v-card {
  border-radius: 0;
//  height: 576px;
}

.apio-button {
  width: 100%;
}

.registration {
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;

  &__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-getstarted {
      width: 304px;
      margin-right: 32px;
    }

    &-signin {
      width: 93px;
    }
  }

  &__weak-strong {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  &__header {
    display: flex;
    width: 660px;
    margin-bottom: 24px;

    &-logo {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-right: 24px;
      font-size: 30px;
      font-weight: bold;
    }
  }

  &__reset-text {
    display: flex;
    align-items: flex-start;
    flex-direction: column;
    &--subtitle {
      color: var(--apio-shade-2);
    }
  }
}

@media screen and (max-width: 767px) {
  .v-sheet.v-card {
    width: 100%;
    margin: 0px;
  }

  ::v-deep .Password {
    max-width: 768px;
    &__strength-meter {
      margin: 8px 0 4px;
    }
  }

  .registration {
    margin: 0px;

    &__header {
      display: flex;
      width: 100%;

      &-logo {
        display: flex;
        align-items: flex-center;
        width: 44px;
        margin-right: 28px;
        margin-bottom: 16px;
        font-size: 30px;
        font-weight: bold;
      }
    }

    &__actions {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;

      &-getstarted {
        width: 100%;
        margin-bottom: 32px;
        margin-right: 0;
      }

      &-signin {
        margin-top: 16px;
        width: 100%;
      }
    }

    .v-card {
      margin: 15px;
    }

    &__reset-text {
      margin: 0px;
      display: flex;
      align-items: flex-end;
    }

    &--form {
      position: relative;
      padding-top: 20px;
    }
  }
}

@media screen and (min-width: 768px) {
  .v-sheet.v-card {
    padding: 24px 20px 20px;
    width: 100%;
  }

  ::v-deep .Password {
    max-width: 768px;
    &__strength-meter {
      margin: 8px 0 4px;
    }
  }

  .registration {
    width: 708px;
    margin: 20px;
    &__header {
      display: flex;
      flex-direction: row;
      width: 100%;

      &-logo {
        display: flex;
        flex-direction: column;
        align-items: flex-center;
        width: 64px;
        margin-right: 24px;
        margin-bottom: 16px;
        font-size: 30px;
        font-weight: bold;
      }
    }

    &__actions {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;

      &-getstarted {
        width: 100%;
        margin-bottom: 32px;
        margin-right: 0;
      }

      &-signin {
        margin-top: 16px;
        width: 100%;
      }
    }

    &__reset-text {
      display: flex;
      align-items: flex-start;
    }

    &--form {
      position: relative;
      padding: 0;
    }
  }
}
</style>
